import { ModelSymmetry } from '../../mol-model-formats/structure/property/symmetry';
import { PluginBehavior } from '../../mol-plugin/behavior/behavior';
import { PluginContext } from '../../mol-plugin/context';
import { PluginStateObject } from '../../mol-plugin-state/objects';
import { TunnelStateObject, TunnelsStateObject } from '../../extensions/sb-ncbr/tunnels/data-model';
import { createAnalysisTools } from './tools/analysis';
import { createAnalysisMapTools, createComparativeAnalysisTools } from './tools/analysis-maps';
import { createAppearanceTools, createRenderTools } from './tools/appearance';
import { createExtensionScienceTools } from './tools/extension-science';
import { createGroundingTools } from './tools/grounding';
import { createMeasurementTools } from './tools/measurement';
import { createModelDataTools } from './tools/model-data';
import { createNavigationTools } from './tools/navigation';
import { createQualityTools } from './tools/quality';
import { createSafetyTools, WebMcpSafetyServices, WebMcpUndoRedoState } from './tools/safety';
import { createSessionTools } from './tools/session';
import { createTrajectoryTools } from './tools/trajectory';
import { createVolumeTools } from './tools/volume';
import { createStructureScienceTools } from './tools/structure-science';
import { createWorkspaceTools, WorkspaceSafety } from './tools/workspace';
import { WebMcpActivityLog, subscribeWebMcpActivity } from './activity';
import { WebMcpProposalStore } from './proposals';
import { WebMcpProvenanceLog } from './provenance';
import { ToolDefinition, WebMcpCapabilities, WebMcpRequest, WebMcpToolRegistration, WebMcpValue } from './registration';
import { ExpandResultCache, failure, toMcpResult } from './result';
import { selectorPropertySchema } from './selector';

function retainAvailability(definition: ToolDefinition, predicate: (capabilities: WebMcpCapabilities) => boolean): ToolDefinition {
    const isAvailable = definition.isAvailable;
    return {
        ...definition,
        isAvailable: capabilities => (isAvailable === void 0 || isAvailable(capabilities)) && predicate(capabilities),
    };
}

function applyIntegrationAvailability(definitions: readonly ToolDefinition[]): readonly ToolDefinition[] {
    return definitions.map(definition => {
        switch (definition.name) {
            case 'expand-result': return { ...definition, isAvailable: () => true };
            case 'get-trajectory-metadata': return retainAvailability(definition, capabilities => capabilities.hasTrajectory || capabilities.hasCoordinates);
            case 'get-trajectory-state':
            case 'set-trajectory-frame':
            case 'play-trajectory':
            case 'stop-animation':
            case 'set-model': return retainAvailability(definition, capabilities => capabilities.hasTrajectory);
            case 'set-assembly': return retainAvailability(definition, capabilities => capabilities.hasAssemblies);
            case 'restore-visibility': return retainAvailability(definition, capabilities => capabilities.isolateActive);
            case 'get-tunnels': return retainAvailability(definition, capabilities => capabilities.hasTunnelState);
            case 'apply-proposal':
            case 'reject-proposal': return retainAvailability(definition, capabilities => capabilities.hasPendingProposals);
            default: return structureDependentToolNames.has(definition.name)
                ? retainAvailability(definition, capabilities => capabilities.hasStructure)
                : definition;
        }
    });
}

const specialistToolNames = [
    'get-anisotropic-factors', 'get-assembly-symmetry', 'get-backbone-dihedrals', 'get-coordination-sites', 'get-cross-link-violations',
    'get-dnatco-steps', 'get-glycans', 'get-principal-axes', 'get-sifts-mapping', 'get-struct-conn', 'get-helix-orientation', 'get-low-confidence-regions', 'get-membrane-orientation', 'get-partial-charge-summary',
    'get-ring-systems', 'get-validation-outliers', 'get-valence-summary',
] as const;

type SpecialistToolName = typeof specialistToolNames[number];

function isSpecialistToolName(value: WebMcpValue | undefined): value is SpecialistToolName {
    return typeof value === 'string' && specialistToolNames.some(name => name === value);
}

function verifySpecialistSchema(definition: ToolDefinition) {
    const schema = definition.inputSchema;
    if (!('type' in schema) || schema.type !== 'object') throw new Error(`Specialist tool '${definition.name}' must use an object input schema.`);
    const properties = Object.keys(schema.properties ?? {});
    if (properties.some(name => name !== 'target' && name !== 'threshold') || (schema.required ?? []).some(name => name !== 'target')) {
        throw new Error(`Specialist tool '${definition.name}' has an argument missing from run-specialist-analysis.`);
    }
}

function consolidateSpecialistTools(definitions: readonly ToolDefinition[]): readonly ToolDefinition[] {
    const specialists = definitions.filter(definition => specialistToolNames.some(name => name === definition.name));
    if (specialists.length !== specialistToolNames.length) throw new Error('WebMCP specialist tool inventory is incomplete.');
    for (const specialist of specialists) verifySpecialistSchema(specialist);
    const operationDescriptions = specialists.map(definition => `${definition.name}: ${definition.description}`).join('\n');
    const dispatcher: ToolDefinition = {
        name: 'run-specialist-analysis',
        description: `Run one specialist structural analysis. ${operationDescriptions}`,
        inputSchema: {
            type: 'object',
            properties: {
                operation: { enum: specialistToolNames },
                target: selectorPropertySchema,
                threshold: { type: 'number', minimum: 0, maximum: 100, description: 'Optional pLDDT threshold for get-low-confidence-regions only.' },
            },
            required: ['operation', 'target'],
            additionalProperties: false,
        },
        annotations: { readOnlyHint: true, openWorldHint: true },
        isAvailable: capabilities => specialists.some(definition => definition.isAvailable?.(capabilities) !== false),
        execute: (input: WebMcpRequest, signal: AbortSignal) => {
            if (!isSpecialistToolName(input.operation)) return toMcpResult(failure('Unknown specialist analysis operation.', `Choose one of: ${specialistToolNames.join(', ')}.`));
            const definition = specialists.find(candidate => candidate.name === input.operation);
            if (!definition) return toMcpResult(failure('Specialist analysis is unavailable.', 'Refresh WebMCP tool discovery and retry.'));
            const forwarded = { ...input };
            delete forwarded.operation;
            return definition.execute(forwarded, signal);
        },
    };
    return [...definitions.filter(definition => !specialistToolNames.some(name => name === definition.name)), dispatcher];
}

export function assertUniqueToolNames(definitions: readonly ToolDefinition[]) {
    const names = new Set<string>();
    for (const definition of definitions) {
        if (names.has(definition.name)) throw new Error(`Duplicate WebMCP tool definition: '${definition.name}'.`);
        names.add(definition.name);
    }
}

export function createWebMcpToolDefinitions(plugin: PluginContext, services: WebMcpSafetyServices, workspaceSafety: WorkspaceSafety) {
    return consolidateSpecialistTools(applyIntegrationAvailability([
        ...createSafetyTools(services), ...createGroundingTools(plugin, services.expandResults, services), ...createAnalysisTools(plugin, services.expandResults),
        ...createAnalysisMapTools(plugin, services.expandResults), ...createComparativeAnalysisTools(plugin, services), ...createModelDataTools(plugin, services.expandResults),
        ...createStructureScienceTools(plugin, services.expandResults), ...createExtensionScienceTools(plugin, services.expandResults), ...createTrajectoryTools(plugin, services),
        ...createNavigationTools(plugin, services), ...createAppearanceTools(plugin, services), ...createRenderTools(plugin, services), ...createVolumeTools(plugin, services),
        ...createQualityTools(plugin, services.expandResults), ...createMeasurementTools(plugin, workspaceSafety), ...createSessionTools(plugin, workspaceSafety), ...createWorkspaceTools(plugin, workspaceSafety),
    ]));
}

const structureDependentToolNames = new Set([
    'get-struct-conn', 'get-structure-metadata', 'get-missing-residues', 'get-crystal-symmetry', 'get-temperature-factors', 'get-sifts-mapping',
    'get-glycans', 'get-coordination-sites', 'get-principal-axes', 'get-ring-systems', 'align-sequences', 'get-backbone-dihedrals', 'get-sasa-summary', 'get-valence-summary',
    'get-membrane-orientation', 'get-partial-charge-summary', 'get-low-confidence-regions', 'get-assembly-symmetry', 'get-dnatco-steps', 'get-cross-link-violations', 'get-validation-outliers',
    'get-interaction-network', 'get-target-interactions', 'get-tm-alignment-map', 'get-ligand-core-map', 'apply-superposition',
    'get-anisotropic-factors', 'get-helix-orientation',
]);

/** Dynamically exposes only the WebMCP tools supported by the active viewer state. */
export const WebMcpBehavior = PluginBehavior.create({
    name: 'webmcp', category: 'misc', display: { name: 'WebMCP' },
    ctor: class extends PluginBehavior.Handler<{}> {
        private registration: WebMcpToolRegistration | undefined;
        private unsubscribeActivity: (() => void) | undefined;
        private unsubscribeProposals: (() => void) | undefined;
        private services: WebMcpSafetyServices | undefined;

        register() {
            const undoRedo = new WebMcpUndoRedoState(this.ctx);
            const provenance = new WebMcpProvenanceLog();
            const activity = new WebMcpActivityLog();
            const proposals = new WebMcpProposalStore(undoRedo, provenance);
            const expandResults = new ExpandResultCache();
            proposals.registerExecutor({
                action: 'remove-structure',
                revalidate: async args => this.ctx.managers.structure.hierarchy.current.structures.some(entry => entry.cell.transform.ref === args.structureRef)
                    ? { ok: true }
                    : { ok: false, error: `Structure '${args.structureRef}' is no longer loaded.`, hint: 'Refresh the workspace and create a new proposal.' },
                execute: async args => {
                    const structure = this.ctx.managers.structure.hierarchy.current.structures.find(entry => entry.cell.transform.ref === args.structureRef);
                    if (!structure) throw new Error(`Structure '${args.structureRef}' is no longer loaded.`);
                    await this.ctx.managers.structure.hierarchy.remove([structure], true);
                    return { summary: `Removed structure ${args.structureRef}.` };
                }
            });
            proposals.registerExecutor({
                action: 'remove-component',
                revalidate: async args => this.ctx.managers.structure.hierarchy.current.structures.flatMap(structure => structure.components).some(entry => entry.cell.transform.ref === args.componentRef)
                    ? { ok: true }
                    : { ok: false, error: `Component '${args.componentRef}' is no longer loaded.`, hint: 'Refresh the workspace and create a new proposal.' },
                execute: async args => {
                    const component = this.ctx.managers.structure.hierarchy.current.structures.flatMap(structure => structure.components).find(entry => entry.cell.transform.ref === args.componentRef);
                    if (!component) throw new Error(`Component '${args.componentRef}' is no longer loaded.`);
                    await this.ctx.managers.structure.hierarchy.remove([component], true);
                    return { summary: `Removed component ${args.componentRef}.` };
                }
            });
            proposals.registerExecutor({
                action: 'remove-representation',
                revalidate: async args => this.structureRepresentations().some(entry => entry.cell.transform.ref === args.representationRef)
                    ? { ok: true }
                    : { ok: false, error: `Representation '${args.representationRef}' is no longer loaded.`, hint: 'Refresh the workspace and create a new proposal.' },
                execute: async args => {
                    const representation = this.structureRepresentations().find(entry => entry.cell.transform.ref === args.representationRef);
                    if (!representation) throw new Error(`Representation '${args.representationRef}' is no longer loaded.`);
                    await this.ctx.managers.structure.hierarchy.remove([representation], true);
                    return { summary: `Removed representation ${args.representationRef}.` };
                }
            });
            proposals.registerExecutor({ action: 'reset-workspace', revalidate: async () => ({ ok: true }), execute: async () => { await this.ctx.clear(); return { summary: 'Reset workspace.' }; } });
            proposals.registerExecutor({ action: 'revert-to', revalidate: async args => this.ctx.managers.snapshot.getEntry(args.snapshotId) ? { ok: true } : { ok: false, error: 'Snapshot was not found.', hint: 'Save a viewpoint and create a new proposal.' }, execute: async args => { const snapshot = this.ctx.managers.snapshot.setCurrent(args.snapshotId); if (!snapshot) throw new Error('Snapshot was not found.'); await this.ctx.state.setSnapshot(snapshot); return { summary: `Reverted to snapshot ${args.snapshotId}.` }; } });
            proposals.registerExecutor({ action: 'clear-user-annotations', revalidate: async () => { const state = this.ctx.managers.structure.measurement.state; return state.labels.length + state.distances.length + state.angles.length + state.dihedrals.length + state.orientations.length + state.planes.length > 0 ? { ok: true } : { ok: false, error: 'No user annotations remain.', hint: 'Add annotations before creating a new proposal.' }; }, execute: async () => { const state = this.ctx.managers.structure.measurement.state; await this.ctx.managers.structure.hierarchy.remove([...state.labels, ...state.distances, ...state.angles, ...state.dihedrals, ...state.orientations, ...state.planes].map(cell => cell.transform.ref), true); return { summary: 'Cleared user annotations.' }; } });
            const services: WebMcpSafetyServices = { undoRedo, provenance, activity, proposals, expandResults };
            this.services = services;
            this.unsubscribeActivity = subscribeWebMcpActivity(this.ctx, activity);
            const workspaceSafety: WorkspaceSafety = {
                services,
                propose: (args, summary) => proposals.propose('agent', args, summary).id,
            };
            const definitions = createWebMcpToolDefinitions(this.ctx, services, workspaceSafety);
            assertUniqueToolNames(definitions);
            this.registration = WebMcpToolRegistration.create(document, definitions);
            if (!this.registration) {
                this.ctx.log.warn('WebMCP unavailable: the host did not provide document.modelContext.');
                return;
            }
            this.unsubscribeProposals = proposals.subscribe(() => this.updateCapabilities());
            this.subscribeObservable(this.ctx.managers.structure.hierarchy.behaviors.selection, () => this.updateCapabilities());
            this.subscribeObservable(this.ctx.state.data.events.cell.stateUpdated, () => this.updateCapabilities());
            this.subscribeObservable(this.ctx.state.data.events.cell.created, () => this.updateCapabilities());
            this.subscribeObservable(this.ctx.state.data.events.cell.removed, () => this.updateCapabilities());
            this.subscribeObservable(this.ctx.managers.volume.hierarchy.behaviors.selection, () => this.updateCapabilities());
            this.updateCapabilities();
        }
        unregister() { this.registration?.dispose(); this.registration = void 0; this.unsubscribeActivity?.(); this.unsubscribeActivity = void 0; this.unsubscribeProposals?.(); this.unsubscribeProposals = void 0; this.services = void 0; }
        dispose() { this.unregister(); super.dispose(); }


        private structureRepresentations() {
            return this.ctx.managers.structure.hierarchy.current.structures.flatMap(structure => [
                ...(structure.genericRepresentations ?? []),
                ...structure.components.flatMap(component => [...component.representations, ...(component.genericRepresentations ?? [])]),
            ]);
        }

        private updateCapabilities() {
            const registration = this.registration;
            if (!registration) return;
            const hierarchy = this.ctx.managers.structure.hierarchy.current;
            const structures = hierarchy.structures;
            const visibleStructureCount = structures.filter(structure => structure.components.some(component => !component.cell.state.isHidden)).length;
            const models = hierarchy.models.flatMap(model => model.cell.obj?.data ? [model.cell.obj.data] : []);
            const capabilities: WebMcpCapabilities = {
                structureCount: structures.length, hasStructure: structures.length > 0,
                hasVolume: this.ctx.managers.volume.hierarchy.current.volumes.length > 0,
                hasTrajectory: hierarchy.trajectories.length > 0,
                hasCoordinates: [...this.ctx.state.data.cells.values()].some(cell => cell.obj?.type === PluginStateObject.Molecule.Coordinates.type),
                hasTunnelState: [...this.ctx.state.data.cells.values()].some(cell => cell.obj instanceof TunnelStateObject || cell.obj instanceof TunnelsStateObject),
                hasPdbEntry: models.some(model => model.entryId.length === 4),
                hasAssemblies: models.some(model => (ModelSymmetry.Provider.get(model)?.assemblies.length ?? 0) > 0),
                isolateActive: structures.length > 1 && visibleStructureCount === 1,
                hasPendingProposals: this.services !== void 0 && this.services.proposals.getPending().length !== 0,
            };
            void registration.update(capabilities).catch(error => this.ctx.log.error(`WebMCP registry update failed: ${error instanceof Error ? error.message : String(error)}`));
        }
    }, params: () => ({}),
});
